import {
  pageSizesType,
  pageTypeSizeOptions,
  pageTypeSizes,
} from '@/types/PageSize';
import './style.less';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';
import React, { lazy, useLayoutEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useDisclosure, useRouteMatchParam } from '@/hooks';
import {
  FloatingFormItem,
  Group,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { useCompanyReportConfig } from '@/core/domain/enterprise/Companies/CompaniesReportConfig/CompaniesReportConfig.hooks';
import { DefaultName } from '@/core/domain/reports/company/Company.types';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import moment from 'moment';
import { useZipCode } from '@/core/domain/zipcode/ZipCodes/ZipCodes.hooks';
import { applyCpfCnpjMask, applyMask } from '@/utils/mask';
import CpfCnpjInput from '@/components/_common/CpfCnpjInput';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

export interface ReceiptRecordFormValues {
  pageTypeSize: string;
  pageSize: { x: number; y: number };
  orientation: 'landscape' | 'portrait';
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

const initialState: ReceiptRecordFormValues = {
  pageTypeSize: 'A4',
  pageSize: pageSizesType['A4'],
  orientation: 'portrait',
  margin: 10,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
};

const STORAGE_KEY = 'receiptRecordConfig';

const initialStorage = localStorage.getItem(STORAGE_KEY);

const initialFormValues = {
  ...initialState,
  ...(initialStorage ? JSON.parse(initialStorage) : {}),
};

export default function PrivateReportReceiptRecord() {
  const match = useRouteMatch();
  const companyIdParam = useRouteMatchParam('companyId');

  const [form] = Form.useForm<ReceiptRecordFormValues>();
  const formValues = Form.useWatch([], form);

  const isLandscape = formValues?.orientation === 'landscape';
  const hasPageSizeTypeCustom =
    formValues?.pageTypeSize === pageTypeSizes['Custom'];

  const [drawerConfigOpen, drawerConfigAction] = useDisclosure(false);

  async function validateFields() {
    await form.validateFields();
  }

  const company = useCompany(Number(companyIdParam));
  const configCompany = useCompanyReportConfig(companyIdParam);
  const zipCode = useZipCode(company.data?.companyAddress.zip);

  const dateDefault = moment();

  useLayoutEffect(() => {
    const css = `
        @media print {
          .print-area {
            border: none !important;
            padding: 0px !important;
            width: 100% !important;
          }
  
  
          @page {
            size: ${
              hasPageSizeTypeCustom
                ? `${formValues?.pageSize[isLandscape ? 'y' : 'x'] || 0}mm ${
                    formValues?.pageSize[isLandscape ? 'x' : 'y'] || 0
                  }mm`
                : formValues?.pageTypeSize || 'auto'
            } ${!hasPageSizeTypeCustom ? formValues?.orientation : ''};
  
            margin-top: ${formValues?.marginTop || 0}mm;
            margin-bottom: ${formValues?.marginBottom || 0}mm;
            margin-left: ${formValues?.marginLeft || 0}mm;
            margin-right: ${formValues?.marginRight || 0}mm;
          }
      `;

    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);

    style.setAttribute('type', 'text/css');
    style.setAttribute('id', 'receipt-record-style-print');

    style.appendChild(document.createTextNode(css));

    return () =>
      document.getElementById('receipt-record-style-print')?.remove();
  }, [formValues]);

  const phone = company.data?.contactList.find(
    (contact) => contact.contactType === ContactType.Phone,
  )?.value;
  const mobile = company.data?.contactList.find(
    (contact) => contact.contactType === ContactType.Mobile,
  )?.value;
  const email = company.data?.contactList.find(
    (contact) => contact.contactType === ContactType.Email,
  )?.value;

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {(company.isLoading || configCompany.isLoading || zipCode.isLoading) && (
        <ModalLoadingOverlay
          messages={
            company.isLoading
              ? ['Carregando dados da empresa...']
              : configCompany.isLoading
              ? ['Carregando configurações...']
              : zipCode.isLoading
              ? ['Buscando endereço da empresa...']
              : ['Carregando...']
          }
        />
      )}

      {match.isExact && !!company.data && !!configCompany.data && (
        <TemplateReport style={{ margin: '0px', width: '100%' }}>
          <Form
            form={form}
            initialValues={initialFormValues}
            onValuesChange={(_, values) =>
              localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
            }
          >
            <main className="receipt-record">
              <div className="display-none-to-print">
                <div className="receipt-record-header">
                  <Row>
                    <Col span={24} md={6}>
                      <h2>Gerar Recibo em branco</h2>
                    </Col>

                    <Col span={24} md={18}>
                      <SpaceForButton justifyContent="flex-end">
                        <Button block onClick={drawerConfigAction.open}>
                          Configurar impressão
                        </Button>

                        <Button
                          block
                          className="button-primary"
                          type="primary"
                          onClick={() => window.print()}
                        >
                          Imprimir
                        </Button>
                      </SpaceForButton>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="receipt-record-content">
                <div className="print-page-info">
                  <span>
                    (
                    {hasPageSizeTypeCustom
                      ? 'Customizado'
                      : formValues?.pageTypeSize}
                    ){' '}
                    {`${
                      isLandscape
                        ? formValues?.pageSize?.y || 0
                        : formValues?.pageSize?.x || 0
                    }mmX${
                      isLandscape
                        ? formValues?.pageSize?.x || 0
                        : formValues?.pageSize?.y || 0
                    }mm`}{' '}
                    {formValues?.orientation === 'portrait'
                      ? 'Retrato'
                      : 'Paisagem'}
                  </span>
                </div>

                <div
                  className="print-area"
                  id="print-area"
                  style={{
                    paddingTop: `${formValues?.marginTop || 0}mm`,
                    paddingRight: `${formValues?.marginRight || 0}mm`,
                    paddingBottom: `${formValues?.marginBottom || 0}mm`,
                    paddingLeft: `${formValues?.marginLeft || 0}mm`,
                    width: formValues?.pageSize
                      ? `${
                          isLandscape
                            ? formValues?.pageSize?.y || 0
                            : formValues?.pageSize?.x || 0
                        }mm`
                      : undefined,
                  }}
                >
                  <div className="receipt-header">
                    <Card
                      style={{ background: 'white', borderColor: '#d9d9d9' }}
                    >
                      <Row align="middle">
                        <Col flex={1}>
                          <Group gap={24}>
                            {!!company.data?.logo && (
                              <Image
                                preview={false}
                                src={company.data?.logo}
                                width={80}
                              />
                            )}

                            <div>
                              {!!company.data?.name && (
                                <Typography.Title level={4} className="m-0 p-0">
                                  {configCompany.data?.companyNaming ===
                                  DefaultName.fantasyName
                                    ? company.data?.nickName
                                    : company.data?.name}
                                </Typography.Title>
                              )}

                              {!!company.data?.companyAddress && (
                                <div>
                                  {`${company.data.companyAddress.street} - ${company.data.companyAddress.number} - ${company.data.companyAddress.district}`}
                                </div>
                              )}

                              {!!zipCode.data && (
                                <div>
                                  {`${zipCode.data.cityName} - ${zipCode.data.state} - ${zipCode.data.zipCode}`}
                                </div>
                              )}

                              {!!company.data?.cnpj && (
                                <div>
                                  {`CNPJ: ${applyCpfCnpjMask(
                                    company.data.cnpj,
                                  )}`}
                                </div>
                              )}
                            </div>
                          </Group>
                        </Col>

                        <Col>
                          <Group
                            align="center"
                            position="center"
                            style={{ flexDirection: 'column' }}
                            gap={20}
                          >
                            <div>
                              <p>
                                <strong>Nº Recibo</strong>
                              </p>
                            </div>

                            <div>
                              <p>{dateDefault.format('DD/MM/YYYY HH:mm:ss')}</p>
                            </div>
                          </Group>
                        </Col>
                      </Row>
                    </Card>
                  </div>

                  <br />

                  <Form form={form} layout="vertical">
                    <Row gutter={16}>
                      <Col span={16}>
                        <FloatingFormItem label="Pago por">
                          <Input />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="CPF/CNPJ">
                          <CpfCnpjInput />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={16}>
                        <FloatingFormItem label="Recebido por">
                          <InputText />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="CPF/CNPJ">
                          <CpfCnpjInput />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={6}>
                        <FloatingFormItem label="Valor bruto de">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Desconto/Acréscimo de">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Total recebido">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Pago em">
                          <Input />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={8}>
                        <div style={{ fontSize: 10 }}>
                          <div>
                            {`Telefone: ${
                              !!phone ? applyMask(phone, cellphoneMask) : '-'
                            }`}
                          </div>

                          <div>
                            {`Celular: ${
                              !!mobile ? applyMask(mobile, cellphoneMask) : '-'
                            }`}
                          </div>

                          <div>{`Email: ${!!email ? email : '-'}`}</div>
                        </div>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="Referente a(ao)">
                          <InputText />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="Identificado por">
                          <InputText />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <br />
                    <hr style={{ border: '1px dashed #ddd' }} />
                    <br />

                    <Row gutter={16}>
                      <Col span={16}>
                        <FloatingFormItem label="Pago por">
                          <Input />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="CPF/CNPJ">
                          <CpfCnpjInput />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={16}>
                        <FloatingFormItem label="Recebido por">
                          <InputText />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem label="CPF/CNPJ">
                          <CpfCnpjInput />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={6}>
                        <FloatingFormItem label="Valor bruto de">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Desconto/Acréscimo de">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Total recebido">
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Pago em">
                          <Input />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={6}>
                        <FloatingFormItem label="Plano de contas">
                          <Input />
                        </FloatingFormItem>
                      </Col>
                      <Col span={6}>
                        <FloatingFormItem label="Natureza">
                          <InputText />
                        </FloatingFormItem>
                      </Col>
                      <Col span={6}>
                        <FloatingFormItem label="Tipo de documento">
                          <InputText />
                        </FloatingFormItem>
                      </Col>

                      <Col span={6}>
                        <FloatingFormItem label="Conta">
                          <Input />
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={12}>
                        <div>
                          <p style={{ color: 'red' }}>Via da empresa</p>

                          <div>
                            {!!company.data?.name && (
                              <strong>
                                {configCompany.data?.companyNaming ===
                                DefaultName.fantasyName
                                  ? company.data?.nickName
                                  : company.data?.name}
                              </strong>
                            )}

                            {!!company.data?.companyAddress && (
                              <div>
                                {`${company.data.companyAddress.street} - ${company.data.companyAddress.number} - ${company.data.companyAddress.district}`}
                              </div>
                            )}

                            {!!zipCode.data && (
                              <div>
                                {`${zipCode.data.cityName} - ${zipCode.data.state} - ${zipCode.data.zipCode}`}
                              </div>
                            )}

                            {!!company.data?.cnpj && (
                              <div>
                                {`CNPJ: ${applyCpfCnpjMask(company.data.cnpj)}`}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>

                      <Col span={12}>
                        <Row>
                          <Col span={24}>
                            <FloatingFormItem label="Referente a(ao)">
                              <InputText />
                            </FloatingFormItem>
                          </Col>

                          <Col span={24}>
                            <FloatingFormItem label="Identificado por">
                              <InputText />
                            </FloatingFormItem>
                          </Col>

                          <Col span={24}>
                            <FloatingFormItem label="Assinatura">
                              <InputText />
                            </FloatingFormItem>
                            <p>
                              Emissão:{' '}
                              {dateDefault.format('DD/MM/YYYY HH:mm:ss')}
                            </p>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </div>
            </main>

            <Drawer
              title="Configuração de impressão"
              placement="right"
              open={drawerConfigOpen}
              onClose={drawerConfigAction.close}
              destroyOnClose={false}
              width={600}
            >
              <div className="m-b-20">
                <strong>Layout da página</strong>
              </div>

              <Row gutter={[16, 16]}>
                <Col span={24} md={12}>
                  <FloatingFormItem
                    label="Tipo de impressão"
                    name="pageTypeSize"
                    getValueFromEvent={(value) => {
                      if (value !== pageTypeSizes['Custom']) {
                        form.setFieldsValue({
                          pageSize: pageSizesType[value],
                        });
                      }
                      validateFields();
                      return value;
                    }}
                  >
                    <Select
                      options={pageTypeSizeOptions}
                      style={{ width: '100%' }}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem label="Orientação" name="orientation">
                    <Select
                      options={[
                        { value: 'landscape', label: 'Paisagem' },
                        { value: 'portrait', label: 'Retrato' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem
                    label="Largura da página (mm)"
                    name={['pageSize', 'x']}
                    rules={[
                      {
                        type: 'number',
                        required: formValues?.pageTypeSize === 'Custom',
                        message: '',
                      },
                    ]}
                  >
                    <InputDecimal
                      style={{ width: '100%' }}
                      min={0}
                      disabled={!hasPageSizeTypeCustom}
                    />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem
                    label="Altura da página (mm)"
                    name={['pageSize', 'y']}
                    rules={[
                      {
                        type: 'number',
                        required: formValues?.pageTypeSize === 'Custom',
                        message: '',
                      },
                    ]}
                  >
                    <InputDecimal
                      style={{ width: '100%' }}
                      min={0}
                      disabled={!hasPageSizeTypeCustom}
                    />
                  </FloatingFormItem>
                </Col>
              </Row>

              <Divider />

              <div className="m-b-20">
                <strong>Margens da folha</strong>
              </div>

              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <FloatingFormItem
                    label="Todo os lados (mm)"
                    name="allMarginSides"
                    getValueFromEvent={(value) => {
                      form.setFieldsValue({
                        marginBottom: value,
                        marginLeft: value,
                        marginRight: value,
                        marginTop: value,
                      });
                      return value;
                    }}
                  >
                    <InputDecimal style={{ width: '100%' }} min={0} max={50} />
                  </FloatingFormItem>
                </Col>

                <Col span={24}>
                  <Row justify="center">
                    <Col span={12}>
                      <FloatingFormItem label="Superiro (mm)" name="marginTop">
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem label="Esquerda (mm)" name="marginLeft">
                    <InputDecimal style={{ width: '100%' }} min={0} max={50} />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem label="Direita (mm)" name="marginRight">
                    <InputDecimal style={{ width: '100%' }} min={0} max={50} />
                  </FloatingFormItem>
                </Col>

                <Col span={24}>
                  <Row justify="center">
                    <Col span={12}>
                      <FloatingFormItem
                        label="Inferiro (mm)"
                        name="marginBottom"
                      >
                        <InputDecimal
                          style={{ width: '100%' }}
                          min={0}
                          max={50}
                        />
                      </FloatingFormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Divider />

              <Button
                onClick={() => {
                  form.resetFields();

                  localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(initialState),
                  );

                  form.setFieldsValue(initialState);
                }}
                type="primary"
                block
              >
                Resetar Configurações
              </Button>
            </Drawer>

            <Form.Item hidden name="pageTypeSize" />
            <Form.Item hidden name="pageSize" />
            <Form.Item hidden name="orientation" />
            <Form.Item hidden name="margin" />
            <Form.Item hidden name="marginTop" />
            <Form.Item hidden name="marginBottom" />
            <Form.Item hidden name="marginLeft" />
            <Form.Item hidden name="marginRight" />
          </Form>
        </TemplateReport>
      )}
    </React.Suspense>
  );
}
